import type { MagicLinkResponse, SignUrlResponse, ApiError } from '@/types';
import { AuthenticationError } from '@/types';
import { getRuntimeConfig } from '@/utils/config';

// Extend window interface to include our runtime config
declare global {
  interface Window {
    CONFIG?: {
      API_BASE_URL?: string;
    };
  }
}

// Get API base URL from runtime configuration
const API_BASE_URL = getRuntimeConfig().API_BASE_URL;

/**
 * Generic API request handler with error handling
 */
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  
  const response = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  });

  if (!response.ok) {
    const errorData: ApiError = {
      message: `HTTP ${response.status}: ${response.statusText}`,
      statusCode: response.status,
    };

    try {
      const errorBody = await response.json();
      errorData.message = errorBody.message || errorData.message;
      errorData.details = errorBody;
    } catch {
      // Use default error message if response body isn't JSON
    }

    if (response.status === 401) {
      throw new AuthenticationError(errorData.message, errorData.statusCode);
    }

    throw new Error(errorData.message);
  }

  return response.json();
}

/**
 * Generate magic link for customer
 */
export async function generateMagicLink(customerId: string): Promise<MagicLinkResponse> {
  return apiRequest<MagicLinkResponse>('/magic-link', {
    method: 'POST',
    body: JSON.stringify({ customerId }),
  });
}

/**
 * Get pre-signed URL for file upload
 */
export async function getSignedUploadUrl(
  filename: string,
  token: string
): Promise<SignUrlResponse> {
  const encodedFilename = encodeURIComponent(filename);
  
  return apiRequest<SignUrlResponse>(`/sign?filename=${encodedFilename}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });
}

/**
 * Verify JWT token with backend
 */
export async function verifyToken(token: string): Promise<{ valid: boolean; customerId?: string }> {
  try {
    const response = await apiRequest<{ valid: boolean; customerId: string }>('/verify', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    return response;
  } catch (error) {
    console.error('Token verification failed:', error);
    // For any error during token verification, return invalid
    return { valid: false };
  }
}

/**
 * Upload file directly to S3 using pre-signed URL
 */
export async function uploadFileToS3(
  file: File,
  signedUrl: string,
  method: string = 'PUT',
  fields?: Record<string, string>,
  onProgress?: (progress: number) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    // Track upload progress
    if (onProgress) {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const percentComplete = (event.loaded / event.total) * 100;
          onProgress(percentComplete);
        }
      });
    }

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}: ${xhr.statusText}`));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Network error during upload'));
    });

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload was aborted'));
    });

    xhr.open(method, signedUrl);

    // For multipart uploads, we need to use FormData
    if (fields && Object.keys(fields).length > 0) {
      const formData = new FormData();
      
      // Add all the fields first
      Object.entries(fields).forEach(([key, value]) => {
        formData.append(key, value);
      });
      
      // Add the file last
      formData.append('file', file);
      
      xhr.send(formData);
    } else {
      // For simple PUT uploads
      xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
      xhr.send(file);
    }
  });
}

/**
 * Health check endpoint
 */
export async function healthCheck(): Promise<{ status: string; timestamp: string }> {
  return apiRequest('/health');
}

/**
 * Get application configuration from backend
 */
export async function getAppConfig(): Promise<{
  allowedFileTypes: string[];
  maxFileSize: number;
  environment: string;
}> {
  try {
    return await apiRequest('/config');
  } catch {
    // Return default config if backend is unavailable
    return {
      allowedFileTypes: ['.sql', '.cs', '.java', '.py'],
      maxFileSize: 50 * 1024 * 1024, // 50MB
      environment: 'development',
    };
  }
}